import { redirect } from 'react-router-dom';

import { refreshAccessToken } from '@apis/API';
import { getMe } from '@apis/users';
import { tokenStorage } from '@utils/tokenStorage';

/**
 * Root loader (checkIfSignIn)
 *
 * Behavior:
 * - If there is no access token, redirect to /login immediately.
 * - If an access token exists, attempt to load the current user via /users/me.
 *   - Success: return `me` (used by Root loader data)
 *   - Failure: return null WITHOUT redirect to avoid "login flicker" on refresh.
 *
 * Notes:
 * - Token refresh is handled by API interceptor (if enabled).
 * - Actual route blocking / redirect is handled by AuthRoute/AdminRoute/PartnerRoute.
 */
export const checkIfSignIn = async () => {
  const accessToken = tokenStorage.getAccessToken();
  const refreshToken = tokenStorage.getRefreshToken();

  // If only the refresh token remains, try to recover the session first.
  if (!accessToken) {
    if (refreshToken) {
      try {
        await refreshAccessToken();
        const me = await getMe();
        return me;
      } catch {
        tokenStorage.clearAll();
      }
    }

    // Definitely not authenticated: redirect to login.
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    return redirect(`/login?returnTo=${returnTo}`);
  }

  try {
    // Authenticated (token exists) + user profile loaded
    const me = await getMe();
    return me;
  } catch {
    // If /me fails:
    // - Do NOT redirect here to prevent flicker during page refresh.
    // - If there is no refresh token, treat it as a dead session and clear tokens.
    if (!refreshToken) {
      tokenStorage.clearAll();
    }

    // Returning null lets Root render without forcing /login,
    // and AuthRoute will decide the next action based on auth state.
    return null;
  }
};
